import type { Styles } from '@adornis/ass/style';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { whenSeen } from '@adornis/chemistry/directives/when-seen.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar';
import { DubniumDialog } from '@adornis/digitale-helden-shared/client/theme/d-dialog.js';
import '@adornis/digitale-helden-shared/client/theme/d-empty-placeholder.js';
import { FileFilter } from '@adornis/digitale-helden-shared/features/mediaverwaltung/db/FileFilter.js';
import { LASFile } from '@adornis/digitale-helden-shared/features/mediaverwaltung/db/LASFile.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import type { XInput } from '@adornis/forms/x-input';
import { QueryPagination } from '@adornis/pagination/paginatable.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { debounceTime, filter, takeUntil } from 'rxjs';
import '../../../client/theme/d-dropdown-multiselection';
import '../../../client/theme/fonts';
import { getFilesPaginatedFiltered } from '../api/getFilesPaginatedFiltered';
import { Folder } from '../db/Folder';
import type { MediaUnion } from '../db/MediaUnion';
import { FileUploadArea } from './FileUploadArea';
import { MediaBreadcrumb } from './MediaBreadcrumb';
import { MediaBundleDialog } from './MediaBundleDialog';
import DragSelect from './helpers/DragSelect.js';
import { deleteMedia } from './helpers/deleteMedia';
import { getFileSize } from './helpers/getFileSize';
import { getMediaPreview } from './helpers/getMediaPreview';
import { getMediaTitle } from './helpers/getMediaTitle';
import { addFolderIcon } from './icons/addFolderIcon';

export enum SupportedFileType {
  PNG = 'png',
  PDF = 'pdf',
  JPG = 'jpg',
  JPNG = 'jpng',
  MP3 = 'mp3',
  MP4 = 'mp4',
  GIF = 'gif',
  WEBP = 'webp',
}

@customElement('las-file-explorer')
export class LASFileExplorer extends ChemistryLitElement {
  @property({ type: Boolean }) selectable = false;
  @property({ type: String }) selectedFileID: Maybe<string>;
  @property({ attribute: false }) allowedFileTypes = new RXController<Array<SupportedFileType>>(this, []);

  @state() private readonly _fileFilter = new FileFilter({ parentFolderID: null });
  @state() private readonly _fileFilterController = new FormController(this, this._fileFilter);

  @state() private readonly _files = new QueryPagination(
    getFilesPaginatedFiltered.bind(LASFile),
    [this._fileFilter],
    {
      _id: 1,
      name: 1,
      meta: { extension: 1, fileName: 1, size: 1, md5: 1, mimetype: 1 },
      altTag: 1,
      createdAt: 1,
      updatedAt: 1,
      downloadName: 1,
    } as BaseQLSelectionSet<LASFile>,
    10,
    10,
  );

  @state() private _fileData = new RXController(
    this,
    this._files.data.pipe(filter(Boolean), takeUntil(this.disconnected)),
  );

  @state() private _renameID: Maybe<string> = null;

  override connectedCallback(): void {
    super.connectedCallback();

    this.allowedFileTypes.observable.pipe(filter(Boolean)).subscribe(allowedTypes => {
      this._fileFilter.fileExtensions = allowedTypes;
    });

    this._fileFilter.whenChanging
      .pipe(filter(Boolean), debounceTime(200), takeUntil(this.disconnected))
      .subscribe(() => {
        this._files.updateParameters(this._fileFilter);
      });
  }

  private _dragSelect: DragSelect | undefined;
  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (!this.selectable) this._updateDragSelect();

    requestAnimationFrame(() => {
      if (!this._renameID) return;
      // Die ID muss für CSS-Selektoren escaped werden
      const escapedID = CSS.escape(this._renameID);
      const renameInput = this.renderRoot.querySelector(`#rename-${escapedID}`) as Maybe<HTMLElement>;
      if (!renameInput) return;
      renameInput.focus();
    });
  }

  private _updateDragSelect() {
    if (!this._dragSelect) {
      this._dragSelect = new DragSelect(this.renderRoot, {});
      this._dragSelect.subscribe('DS:end', async ({ dropTarget, isDragging, items }) => {
        if (!isDragging || !dropTarget) {
          for (const item of items) {
            item.style.transform = 'translate3d(0, 0, 0)';
          }
          return;
        }

        const folderID = dropTarget?.id || null;

        for (const droppedItem of (dropTarget?.itemsDropped ?? []) as HTMLElement[]) {
          const mediaID = droppedItem.dataset.mediaId;
          if (mediaID === folderID) continue;
          const media = this._fileData.value?.find(f => f._id === mediaID);
          if (!media) continue;
          media.parentFolderID = folderID;
          await media.save();
        }

        this._files.updateParameters(this._fileFilter);
      });
    }

    this._dragSelect.setSettings({
      draggability: true,
      selectables: (this._fileData.value ?? []).map(
        f => this.renderRoot.querySelector(`[data-media-id="${CSS.escape(f._id)}"]`)!,
      ),
      dropZones: Array.from(
        this.renderRoot.querySelectorAll(`[data-ds-dropzone="true"]`) as NodeListOf<HTMLElement>,
      ).map(f => ({
        id: f.dataset.mediaId,
        element: f,
      })),
      dropInsideThreshold: 0.5,
      useTransform: true,
    });
  }

  override render() {
    return html`
      <d-flex
        space="md"
        direction="column"
        ${css({
          borderRadius: '16px',
          padding: this.spacing.md,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        })}
      >
        <d-flex horizontal crossaxis-center center>
          <div>
            ${addFolderIcon({
              onClick: async () => {
                const newFolder = new Folder({
                  parentFolderID: this._fileFilter.parentFolderID,
                });
                const createdFolderID = await newFolder.create();
                newFolder._id = createdFolderID;
                this._renameID = createdFolderID;
                this._fileData.next([newFolder, ...(this._fileData.value ?? [])]);
              },
            })}
            <d-tooltip>Ordner erstellen</d-tooltip>
          </div>
        </d-flex>
        ${this._renderToolbar()}
        ${FileUploadArea({
          onValuePicked: async files => {
            const uploadedFiles = await this._uploadFiles(files);
            const lasFiles = await getByID(LASFile, uploadedFiles)({ _id: 1, meta: { fileName: 1, extension: 1 } });
            for (const lasFile of lasFiles) {
              await this._handleEditContent(lasFile as LASFile);
            }
          },
        })}
        ${MediaBreadcrumb({
          parentFolderID: this._fileFilter.parentFolderID,
          onBreadcrumbClick: folderID => {
            this._fileFilter.parentFolderID = folderID;
          },
        })}
        ${this._renderFiles()}
      </d-flex>
    `;
  }

  private _renderToolbar() {
    return html`
      <d-flex
        horizontal
        space="md"
        crossaxis-end
        ${css({
          marginBottom: this.spacing.md,
          padding: `${this.spacing.sm} 0`,
          borderBottom: `1px solid ${this.colors.tone.tertiaryBackground}`,
        })}
      >
        <d-input
          placeholder="Suche nach Dateien..."
          clearable
          flex
          ${css({
            minWidth: '250px',
          })}
          @value-changed=${(e: ValueEvent<string>) => {
            this._fileFilter.fileName = e.detail.value ?? null;
          }}
        >
          <d-icon slot="prefix">search</d-icon>
        </d-input>

        ${this.allowedFileTypes.value.length > 0
          ? nothing
          : html`
              <d-dropdown-multiselection
                ${css({ minWidth: '200px' })}
                placeholder="Dateitypen filtern"
                .selectables=${Object.values(SupportedFileType)}
                ${this._fileFilterController.field('fileExtensions')}
                clearable
              ></d-dropdown-multiselection>
            `}

        <d-button
          @click=${() => {
            if ((this.allowedFileTypes.value.length ?? 0) === 0) this._fileFilter.fileExtensions = null;
            this._fileFilter.fileName = null;
            this._fileFilter.parentFolderID = null;
          }}
        >
          <d-icon solid> broom-wide </d-icon>
          <d-tooltip>Filter zurücksetzen</d-tooltip>
        </d-button>
      </d-flex>
    `;
  }

  private _renderFiles() {
    const fileData = this._fileData.value;
    if (!fileData || fileData.length === 0)
      return html`
        <d-flex
          space="md"
          crossaxis-center
          justify-center
          ${css({
            padding: `${this.spacing.xl} 0`,
            backgroundColor: this.colors.tone.tertiaryBackground,
            borderRadius: '8px',
            minHeight: '200px',
          })}
        >
          <d-empty-placeholder
            no-wrapper
            .text=${'Keine Dateien gefunden'}
            .img=${'do-not-enter'}
          ></d-empty-placeholder>
        </d-flex>
      `;

    return this._renderGridView(fileData);
  }

  private _renderGridView(fileData: MediaUnion[]) {
    return html`
      <d-flex
        ${css({
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: this.spacing.md,
        })}
      >
        ${repeat(
          fileData,
          file => file._id,
          (file, index) => this._renderFileCard(file, index, fileData.length - 1),
        )}
      </d-flex>
    `;
  }

  private _renderFileCard(media: MediaUnion, index: number, maxIndex: number) {
    const isSelected = this.selectedFileID === media._id;

    return html`
      <d-flex
        class=${isSelected ? 'selected' : ''}
        data-ds-dropzone=${media instanceof Folder}
        data-media-id=${media._id}
        ${acss({
          position: 'relative',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          overflow: 'hidden',
          border: `1px solid ${isSelected ? this.colors.tone.primary : this.colors.tone.tertiaryBackground}`,
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
            borderColor: this.colors.tone.primary,
          },
        })}
        @click=${() => this._selectFile(media)}
      >
        ${whenSeen(
          () => {
            if (index !== maxIndex) return;
            this._files.next();
          },
          () => {},
        )}
        ${getMediaPreview(media)}

        <d-flex direction="column" space="xs" ${css({ padding: '0 8px 8px 8px' })}>
          ${this._renderMediaTitle(media)}

          <d-flex horizontal crossaxis-center space="xs">
            ${this._renderFileSize(media)} ${this._renderFileDate(media)}
          </d-flex>

          <d-flex
            horizontal
            space="none"
            justify-end
            padding-top="xs"
            ${css({
              marginTop: '4px',
              borderTop: `1px solid ${this.colors.tone.tertiaryBackground}`,
              paddingTop: '8px',
            })}
          >
            ${this._renderFileActions(media)}
          </d-flex>
        </d-flex>
      </d-flex>
    `;
  }

  private _renderMediaTitle(media: MediaUnion) {
    if (this._renameID === media._id) {
      const saveValue = async (value: string) => {
        if (media instanceof LASFile) {
          media.meta!.fileName = value;
          await media.save();
        }
        if (media instanceof Folder) {
          media.name = value;
          await media.save();
        }
        this._renameID = null;
      };

      return html`
        <d-input
          editable
          id=${`rename-${media._id}`}
          .value=${getMediaTitle(media, true)}
          @keydown=${async (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            e.stopPropagation();
            const value = (e.target as XInput).value.value as string;
            await saveValue(value);
          }}
          @value-picked=${async (e: ValueEvent<string>) => {
            e.preventDefault();
            e.stopPropagation();
            await saveValue(e.detail.value);
          }}
          ${css({
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.9rem',
          })}
        >
          ${getMediaTitle(media)}
        </d-input>
      `;
    }

    return html`
      <d-text
        ${acss({
          textAlign: 'center',
          fontSize: '16px',
          lineHeight: '28px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '250px',
          '&:hover': {
            color: this.colors.tone.primary,
            textDecoration: 'underline',
          },
        })}
        @mousedown=${e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        @click=${e => {
          e.preventDefault();
          e.stopPropagation();

          this._openMedia(media);
        }}
      >
        ${getMediaTitle(media)}
        <d-tooltip> ${getMediaTitle(media)} </d-tooltip>
      </d-text>
    `;
  }

  private _openMedia(media: MediaUnion) {
    if (media instanceof LASFile) {
      goTo(LASFile.getServeLink(media._id), { target: '_blank' });
    }
    if (media instanceof Folder) {
      this._fileFilter.parentFolderID = media._id;
    }
  }

  private _renderFileSize(media: MediaUnion) {
    const size = getFileSize(media);
    if (!size) return nothing;
    return html`
      <d-text
        ${css({
          fontSize: '0.85rem',
          color: this.colors.tone.secondaryText,
        })}
      >
        ${size}
      </d-text>
    `;
  }

  private _renderFileDate(file: MediaUnion) {
    return html`
      <d-text
        ${css({
          fontSize: '0.85rem',
          color: this.colors.tone.secondaryText,
        })}
      >
        ${file.updatedAt
          ? DateTime.fromJSDate(file.updatedAt).toLocaleString(DateTime.DATETIME_MED)
          : DateTime.fromJSDate(file.createdAt).toLocaleString(DateTime.DATETIME_MED)}
      </d-text>
    `;
  }

  private _renderFileActions(media: MediaUnion) {
    const isLASFile = media instanceof LASFile;

    return html`
      <d-flex horizontal space="xs" wrap>
        ${isLASFile
          ? html`
              <d-icon-button
                size="sm"
                icon="copy"
                ${css({
                  color: 'var(--color-red-600)',
                })}
                @mousedown=${e => {
                  e.stopPropagation();
                  e.preventDefault();
                  navigator.clipboard.writeText(LASFile.getServeLink(media._id)).then(() => {
                    XSnackbar.show('Link in Zwischenablage kopiert');
                  });
                }}
              >
                <d-tooltip> In Zwischenablage kopieren </d-tooltip>
              </d-icon-button>

              <d-icon-button
                size="sm"
                icon="gallery-thumbnails"
                ${css({
                  color: this.colors.tone.secondaryText,
                })}
                @mousedown=${e => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                @click=${async e => {
                  e.preventDefault();
                  e.stopPropagation();

                  await MediaBundleDialog({
                    fileReferenceID: media._id,
                    allowedFileTypes: [media.meta?.extension as SupportedFileType],
                  });
                }}
              >
                <d-tooltip>Bundle</d-tooltip>
              </d-icon-button>

              <d-icon-button
                size="sm"
                icon="pencil"
                ${css({
                  color: this.colors.tone.secondaryText,
                })}
                @mousedown=${e => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                @click=${async e => {
                  e.stopPropagation();
                  await this._handleEditContent(media);
                }}
              >
                <d-tooltip>Bearbeiten</d-tooltip>
              </d-icon-button>
            `
          : nothing}

        <d-icon-button
          size="sm"
          icon="input-text"
          ${css({
            color: 'var(--color-red-600)',
          })}
          @mousedown=${e => {
            e.stopPropagation();
            e.preventDefault();
            this._renameID = media._id;
          }}
        >
          <d-tooltip> Umbenennen</d-tooltip>
        </d-icon-button>

        <d-icon-button
          size="sm"
          icon="trash"
          ${css({
            color: 'var(--color-red-600)',
          })}
          @mousedown=${e => {
            e.stopPropagation();
            e.preventDefault();
          }}
          @click=${async e => {
            e.stopPropagation();
            await deleteMedia(media);
            this._fileFilter.fileName = this._fileFilter.fileName ?? null;
            this.requestUpdate();
          }}
        >
          <d-tooltip>Löschen</d-tooltip>
        </d-icon-button>
      </d-flex>
    `;
  }

  private _selectFile(media: MediaUnion) {
    if (!(media instanceof LASFile)) return;
    const fileId = media._id;

    if (!this.selectable) return;
    if (this.selectedFileID === media._id) {
      this.selectedFileID = null;
    } else {
      this.selectedFileID = fileId;
    }
    this.dispatchEvent(new CustomEvent('file-changed', { detail: { value: this.selectedFileID } }));
    this.requestUpdate();
  }

  private async _handleEditContent(file: LASFile) {
    const extension = file.meta?.extension;
    if (!extension) {
      XSnackbar.show('Dateiformat konnte nicht ermittelt werden');
      return;
    }

    let newFile: FileList | File[] | null = null;

    const result = await DubniumDialog.showPopup({
      modal: true,
      content: resolve => html`
        <d-flex
          direction="column"
          gap="md"
          space="md"
          @click=${e => {
            e.stopPropagation();
            e.preventDefault();
          }}
          ${css({
            padding: this.spacing.md,
            maxWidth: '500px',
            backgroundColor: 'white',
            borderRadius: '12px',
          })}
        >
          <d-flex horizontal space="none" crossaxis-center>
            <d-icon
              ${css({
                marginRight: this.spacing.sm,
                color: this.colors.tone.primary,
                fontSize: '1.5rem',
              })}
            >
              pencil
            </d-icon>
            <d-text
              ${css({
                fontSize: '1.4rem',
                fontWeight: 'bold',
              })}
            >
              Datei bearbeiten
            </d-text>
          </d-flex>

          <d-input
            placeholder="Alternativtext (Alt-Tag)"
            .value=${file.altTag}
            @value-changed=${(e: ValueEvent<string>) => {
              file.altTag = e.detail.value || '';
            }}
          ></d-input>
          <d-input
            placeholder="Name des Datei-Downloads"
            .value=${file.downloadName || ''}
            @value-changed=${(e: ValueEvent<string>) => {
              file.downloadName = e.detail.value || '';
            }}
          ></d-input>

          ${FileUploadArea({
            allowedFileTypes: [extension as SupportedFileType],
            title: 'Datei aktualisieren',
            acceptMultipleFiles: false,
            onValuePicked: async files => {
              if (Array.isArray(files) || !files[0]) return;
              const updatedFile = files[0];

              await file.upload(updatedFile).then(() => {
                XSnackbar.show('Datei erfolgreich aktualisiert');
              });
            },
          })}

          <d-flex horizontal w100 space="md" space-between>
            <d-button mode="outline" @click=${() => resolve(false)}> Abbrechen </d-button>
            <d-button @click=${() => resolve(true)}> Speichern </d-button>
          </d-flex>
        </d-flex>
      `,
    });

    if (!result) return;

    const saveChanges = async () => {
      try {
        await file.save();

        // Update file content if a new file was selected
        if (newFile && newFile.length > 0) {
          await file.upload(newFile[0]!);
        }

        return true;
      } catch (error) {
        return false;
      }
    };

    const saveResult = await DubniumDialog.waitFor(saveChanges(), 'Datei wird aktualisiert...');
    if (saveResult) {
      await DubniumDialog.showPopup({
        content: resolve => html`
          <d-flex space="md">
            <d-text> Datei erfolgreich aktualisiert </d-text>
            <d-flex horizontal space="md">
              <d-button variant="secondary" @click=${() => resolve('cancel')}> OK </d-button>
            </d-flex>
          </d-flex>
        `,
      });
    } else {
      await DubniumDialog.showPopup({
        content: resolve => html`
          <d-flex space="md">
            <d-text> Fehler beim Aktualisieren der Datei </d-text>
            <d-flex horizontal space="md">
              <d-button variant="secondary" @click=${() => resolve('cancel')}> OK </d-button>
            </d-flex>
          </d-flex>
        `,
      });
    }
  }

  private async _uploadFiles(files: File[] | FileList) {
    const findFilesByMD5 = async (md5: string) => {
      const filter = new FileFilter({ md5, parentFolderID: '' });

      return getFilesPaginatedFiltered(
        0,
        -1,
        filter,
      )({
        _id: 1,
        meta: { fileName: 1, extension: 1 },
      }).then(res => res.filter(f => f instanceof LASFile));
    };

    const upload = async () => {
      const uploadResults: string[] = [];
      const duplicates: { fileName: string; existingFileName: string; file: File; md5: string; counter: number }[] = [];

      for (const file of files) {
        const fileName = file.name;

        const fileId = await LASFile.upload(file, {
          additionalInfo: {
            ...(this._fileFilter.parentFolderID ? { parentFolderID: this._fileFilter.parentFolderID } : {}),
          },
        });

        if (!fileId) {
          await DubniumDialog.showPopup({
            content: resolve => html`
              <d-flex space="md">
                <d-text> ${fileName} konnte nicht hochgeladen werden </d-text>
                <d-flex horizontal space="md">
                  <d-button variant="secondary" @click=${() => resolve('cancel')}> OK </d-button>
                </d-flex>
              </d-flex>
            `,
          });
          continue;
        }

        const uploadedFile = await getByID(
          LASFile,
          fileId,
        )({
          meta: { md5: 1 },
        });

        if (!uploadedFile || !uploadedFile.meta?.md5) {
          continue;
        }

        const existingFiles = await findFilesByMD5(uploadedFile.meta.md5);

        if (existingFiles && existingFiles.length > 1) {
          const existingFile = existingFiles.find(f => f._id !== fileId);

          if (existingFile) {
            await LASFile.removeFile(fileId)();

            duplicates.push({
              fileName,
              existingFileName: `${existingFile.meta?.fileName}.${existingFile.meta?.extension}`,
              file,
              counter: existingFiles.length - 1,
              md5: uploadedFile.meta.md5,
            });
            continue;
          }
        }

        uploadResults.push(fileId);
      }

      if (duplicates.length > 0) {
        for (const duplicate of duplicates) {
          const result = await DubniumDialog.showPopup({
            content: resolve => html`
              <d-flex space="md">
                <d-text heading4>Datei existiert bereits</d-text>

                <d-text>
                  Existierende Datei: <d-bold ${css({ display: 'inline' })}>${duplicate.existingFileName}</d-bold>
                </d-text>

                <d-flex horizontal space="md" crossaxis-center space-between>
                  <d-button mode="outline" @click=${() => resolve('cancel')}> Abbrechen </d-button>
                  <d-flex horizontal space="md" crossaxis-center>
                    <d-button @click=${() => resolve('rename')}> Unter neuem Namen hochladen </d-button>
                    <d-button @click=${() => resolve('overwrite')}> Überschreiben </d-button>
                  </d-flex>
                </d-flex>
              </d-flex>
            `,
          });

          if (result === 'overwrite') {
            const existingFiles = await findFilesByMD5(duplicate.md5);
            const existingFile = existingFiles[0];
            if (existingFile) {
              const newFileId = await existingFile.upload(duplicate.file, {
                additionalInfo: {
                  ...(this._fileFilter.parentFolderID ? { parentFolderID: this._fileFilter.parentFolderID } : {}),
                },
              });
              existingFile.parentFolderID = this._fileFilter.parentFolderID;
              await existingFile.save();
              if (newFileId) {
                uploadResults.push(newFileId);
              }
            }
          } else if (result === 'rename') {
            const extension = duplicate.fileName.split('.').pop() || '';
            const baseName = duplicate.fileName.replace(`.${extension}`, '');
            let newFileName = `${baseName}-${duplicate.counter}`;

            while (
              (await getFilesPaginatedFiltered(0, -1, new FileFilter({ fileName: newFileName }))({ _id: 1 })).length > 0
            ) {
              duplicate.counter++;
              newFileName = `${baseName}-${duplicate.counter}`;
            }

            newFileName = `${newFileName}.${extension}`;

            const newFile = new File([duplicate.file], newFileName, {
              type: duplicate.file.type,
            });
            const newFileId = await LASFile.upload(newFile, {
              additionalInfo: {
                ...(this._fileFilter.parentFolderID ? { parentFolderID: this._fileFilter.parentFolderID } : {}),
              },
            });
            if (newFileId) {
              uploadResults.push(newFileId);
            }
          }
        }
      }

      return uploadResults;
    };

    const result = await DubniumDialog.waitFor(upload(), 'Datei(en) werden hochgeladen...');

    if (result.length) XSnackbar.show('Datei(en) erfolgreich hochgeladen');
    this._fileFilter.fileName = this._fileFilter.fileName ?? null;
    this.requestUpdate();
    return result;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          userSelect: 'none',
        },
        '.ds-selectable': {
          border: '3px solid #fff',
        },
        '.ds-selected, .selected': {
          color: '#fff',
          background: '#2c2c2e',
          border: '3px solid #0099ff',
        },
        '.ds-dropzone-ready:hover': {
          border: '3px solid #0099ff',
        },
        '.ds-droppable': {
          pointerEvents: 'none',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
        },
      },
    ] as Styles[];
  }
}
